import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  TruckIcon,
  BoltIcon,
  SignalIcon,
  Battery100Icon,
  Battery50Icon,
  Battery0Icon,
  MapPinIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ShieldExclamationIcon,
  FireIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  ClockIcon,
  BuildingOffice2Icon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import {
  VehicleTypeIcon,
  vehicleTypeAccent,
  vehicleTypeLabel,
  VEHICLE_STATUS_LABELS,
  VEHICLE_STATUS_COLORS,
} from '@/constants/vehicleTypes';
import { VEHICLE_ONLINE_THRESHOLD_MS } from '@/constants/connectivity';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { vehiclesApi, alertsApi } from '@/services/api';
import TrackPlayback from '@/components/TrackPlayback';
import { useSocketEvent } from '@/services/socket';
import clsx from 'clsx';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.locale('ar');

const TZ = 'Africa/Tripoli';
const fmt = (input: string | Date | null | undefined, pattern = 'YYYY-MM-DD HH:mm:ss') =>
  input ? dayjs(input).tz(TZ).format(pattern) : '—';
const fmtFromNow = (input: string | Date | null | undefined) =>
  input ? dayjs(input).tz(TZ).fromNow() : '—';

const markerIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

interface IoPosition {
  latitude: string | number;
  longitude: string | number;
  speed?: string | number | null;
  course?: string | number | null;
  altitude?: string | number | null;
  satellites?: number | null;
  ignition?: boolean | null;
  movement?: boolean | null;
  batteryLevel?: number | null;
  externalPower?: boolean | null;
  gsmSignal?: number | null;
  externalVoltageMv?: number | null;
  batteryVoltageMv?: number | null;
  totalOdometerM?: number | null;
  tripOdometerM?: number | null;
  gnssPdop?: string | number | null;
  gnssHdop?: string | number | null;
  dout1?: boolean | null;
  dout2?: boolean | null;
  ioElements?: Record<string, number> | null;
  serverTime: string;
  deviceTime: string;
}

const StatCard = ({
  label, value, sub, icon: Icon, tone = 'gray',
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon: React.ComponentType<{ className?: string }>;
  tone?: 'gray' | 'green' | 'amber' | 'red' | 'blue';
}) => {
  const toneCls = {
    gray:  'from-gray-500 to-gray-600',
    green: 'from-success-500 to-success-600',
    amber: 'from-warning-500 to-warning-600',
    red:   'from-emergency-500 to-emergency-600',
    blue:  'from-primary-500 to-primary-600',
  }[tone];
  return (
    <div className={clsx('card bg-gradient-to-br text-white', toneCls)}>
      <div className="card-body py-3 sm:py-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-white/80 text-xs sm:text-sm">{label}</p>
            <p className="text-xl sm:text-2xl font-bold mt-1">{value}</p>
            {sub && <p className="text-white/70 text-xs mt-1">{sub}</p>}
          </div>
          <Icon className="w-7 h-7 text-white/40" />
        </div>
      </div>
    </div>
  );
};

const SignalBars = ({ level }: { level: number | null | undefined }) => {
  const l = level ?? 0;
  return (
    <div className="flex items-end gap-0.5 h-5">
      {[1, 2, 3, 4, 5].map((b) => (
        <span
          key={b}
          className={clsx(
            'w-1.5 rounded-sm',
            b <= l ? 'bg-white' : 'bg-white/30',
          )}
          style={{ height: `${b * 18}%` }}
        />
      ))}
    </div>
  );
};

export default function VehicleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [liveIo, setLiveIo] = useState<Partial<IoPosition> | null>(null);

  const { data: vehicleResp } = useQuery({
    queryKey: ['vehicle', id],
    queryFn: () => vehiclesApi.getById(id!).then((r) => r.data.data),
    enabled: Boolean(id),
  });

  const { data: ioResp, refetch: refetchIo } = useQuery({
    queryKey: ['vehicle-io-latest', id],
    queryFn: () => vehiclesApi.getLatestIo(id!).then((r) => r.data.data),
    enabled: Boolean(id),
    refetchInterval: 10_000,
  });

  const { data: speedHistory } = useQuery({
    queryKey: ['vehicle-io-history', id, 'speed'],
    queryFn: () => vehiclesApi.getIoHistory(id!, 'speed', 24).then((r) => r.data.data),
    enabled: Boolean(id),
    refetchInterval: 60_000,
  });

  const { data: voltageHistory } = useQuery({
    queryKey: ['vehicle-io-history', id, 'external_voltage_mv'],
    queryFn: () => vehiclesApi.getIoHistory(id!, 'external_voltage_mv', 24).then((r) => r.data.data),
    enabled: Boolean(id),
    refetchInterval: 60_000,
  });

  // Real odometer: the reading recorded for this vehicle plus everything it
  // has covered since. Separate from the IO snapshot, which only carries the
  // tracker's own counter — a number that starts at zero when a device is
  // fitted and says nothing about the vehicle's actual mileage.
  const { data: odometer } = useQuery({
    queryKey: ['vehicle-odometer', id],
    queryFn: () => vehiclesApi.getOdometer(id!).then((r) => r.data.data),
    enabled: Boolean(id),
    refetchInterval: 60_000,
  });

  const { data: alertsResp } = useQuery({
    queryKey: ['vehicle-alerts', id],
    queryFn: () => alertsApi.getAll({ vehicleId: id, limit: 20 }).then((r) => r.data.data),
    enabled: Boolean(id),
    refetchInterval: 30_000,
  });

  // Real-time IO updates over websocket merge into the latest snapshot.
  useSocketEvent('position:update', (msg: any) => {
    if (msg?.vehicleId !== id) return;
    setLiveIo((prev) => ({ ...(prev || {}), ...msg }));
  });

  useEffect(() => {
    if (ioResp) setLiveIo(ioResp);
  }, [ioResp]);

  const io: Partial<IoPosition> = liveIo || ioResp || {};
  const vehicle = vehicleResp;

  const lat = io.latitude !== undefined && io.latitude !== null ? Number(io.latitude) : undefined;
  const lng = io.longitude !== undefined && io.longitude !== null ? Number(io.longitude) : undefined;

  const speedNum = io.speed !== undefined && io.speed !== null ? Number(io.speed) : null;
  const tripKm = io.tripOdometerM ? (io.tripOdometerM / 1000).toFixed(1) : null;

  // Total shown to operators = recorded reading + distance since. Falls back
  // to the device counter only when no reading has ever been entered.
  const deviceOdoKm = io.totalOdometerM ? Math.round(io.totalOdometerM / 1000) : null;
  const odoKm = odometer?.totalKm != null
    ? Math.round(Number(odometer.totalKm))
    : deviceOdoKm;
  const initialKm = odometer?.initialKm != null ? Number(odometer.initialKm) : null;
  const travelledKm = odometer?.travelledKm != null ? Number(odometer.travelledKm) : null;
  // Identity, for the header block.
  const orgName = vehicle?.organization?.nameAr || vehicle?.organization?.name || null;
  const accent = vehicleTypeAccent(vehicle?.vehicleType);
  const typeLabel = vehicleTypeLabel(vehicle?.vehicleType);
  /**
   * The status column is set to 'available' automatically on the first
   * position and flipped to 'offline' on disconnect, so for those two values
   * it says nothing the connectivity chip beside it does not already say —
   * and it showed literally the same words twice. The states an operator
   * actually sets by hand are the ones worth a chip of their own.
   */
  const OPERATOR_SET_STATUSES = new Set(['on_mission', 'maintenance', 'emergency']);
  const statusLabel = vehicle?.status && OPERATOR_SET_STATUSES.has(vehicle.status)
    ? (VEHICLE_STATUS_LABELS[vehicle.status] || vehicle.status) : null;
  const statusColor = vehicle?.status ? VEHICLE_STATUS_COLORS[vehicle.status] : undefined;

  // Connectivity is judged on our receipt time, never the device's clock —
  // a tracker with a drifted RTC is still very much connected.
  const heardAt = io.serverTime || io.deviceTime || null;
  const isOnline = heardAt
    ? Math.abs(Date.now() - new Date(heardAt).getTime()) < VEHICLE_ONLINE_THRESHOLD_MS
    : false;

  const odoSourceLabel = odometer?.travelSource === 'gps'
    ? 'محسوبة من مسار GPS'
    : odometer?.travelSource === 'odometer'
      ? 'من عدّاد جهاز التتبع'
      : null;
  const extV = io.externalVoltageMv ? (io.externalVoltageMv / 1000).toFixed(1) : null;
  const intV = io.batteryVoltageMv ? (io.batteryVoltageMv / 1000).toFixed(2) : null;

  const batteryIcon = useMemo(() => {
    const lvl = io.batteryLevel;
    if (lvl == null) return Battery50Icon;
    if (lvl >= 70) return Battery100Icon;
    if (lvl >= 30) return Battery50Icon;
    return Battery0Icon;
  }, [io.batteryLevel]);

  const batteryTone: 'green' | 'amber' | 'red' = useMemo(() => {
    const lvl = io.batteryLevel;
    if (lvl == null) return 'amber';
    if (lvl >= 50) return 'green';
    if (lvl >= 20) return 'amber';
    return 'red';
  }, [io.batteryLevel]);

  // Active critical event flags — surface chips only when value is "set".
  const criticalChips = useMemo(() => {
    const e = io.ioElements || {};
    const chips: Array<{ label: string; icon: typeof FireIcon; tone: string }> = [];
    if (Number(e['247'] || 0) > 0) chips.push({ label: 'حادث', icon: FireIcon, tone: 'bg-red-100 text-red-800 border-red-300' });
    if (Number(e['246'] || 0) === 1) chips.push({ label: 'قطر', icon: ExclamationTriangleIcon, tone: 'bg-orange-100 text-orange-800 border-orange-300' });
    if (Number(e['285'] || 0) === 1) chips.push({ label: 'تشويش GSM', icon: SignalIcon, tone: 'bg-yellow-100 text-yellow-800 border-yellow-300' });
    if (Number(e['286'] || 0) === 1) chips.push({ label: 'تشويش GPS', icon: SignalIcon, tone: 'bg-yellow-100 text-yellow-800 border-yellow-300' });
    if (Number(e['252'] || 0) === 1) chips.push({ label: 'فُصل من المركبة', icon: ShieldExclamationIcon, tone: 'bg-red-100 text-red-800 border-red-300' });
    if (Number(e['251'] || 0) === 1) chips.push({ label: 'وقوف بمحرك يعمل', icon: ClockIcon, tone: 'bg-blue-100 text-blue-800 border-blue-300' });
    return chips;
  }, [io.ioElements]);

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => navigate('/vehicles')} className="btn btn-outline btn-sm">
          <ChevronLeftIcon className="w-4 h-4" />
          رجوع
        </button>
        <button onClick={() => refetchIo()} className="btn btn-outline btn-sm">
          <ArrowPathIcon className="w-4 h-4" />
          تحديث
        </button>
      </div>

      {/* Identity block.
          The entity leads: operators think in terms of "which hospital owns
          this unit" before anything else, and the old header buried it in a
          grey sub-line next to the raw enum value of the vehicle type. */}
      <div className="card overflow-hidden">
        <div className="h-1.5 w-full" style={{ backgroundColor: accent }} />
        <div className="card-body">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-primary-700">
                <BuildingOffice2Icon className="w-6 h-6 sm:w-7 sm:h-7 flex-shrink-0" />
                <h1 className="text-lg sm:text-2xl font-bold truncate">
                  {orgName || 'جهة غير محددة'}
                </h1>
              </div>

              <div className="mt-3 flex items-center gap-3 min-w-0">
                <span
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl grid place-items-center flex-shrink-0"
                  style={{ backgroundColor: `${accent}1a` }}
                >
                  <VehicleTypeIcon type={vehicle?.vehicleType} className="w-8 h-8 sm:w-9 sm:h-9" />
                </span>
                <div className="min-w-0">
                  {/* Never truncated: the plate IS the vehicle's identity,
                      and a phone-width column was clipping it. */}
                  <p className="text-xl sm:text-3xl font-extrabold text-gray-900 tracking-wide leading-tight break-words">
                    {vehicle?.plateNumber || '...'}
                  </p>
                  <p className="text-sm font-medium" style={{ color: accent }}>{typeLabel}</p>
                </div>
              </div>
            </div>

            {/* State: connectivity first, because a stale reading below is
                only meaningful once you know the unit is still reporting. */}
            <div className="flex flex-col items-start sm:items-end gap-2">
              <span className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium',
                isOnline ? 'bg-success-100 text-success-800' : 'bg-gray-100 text-gray-600',
              )}>
                <span className={clsx('w-2 h-2 rounded-full',
                  isOnline ? 'bg-success-500 animate-pulse' : 'bg-gray-400')} />
                {isOnline ? 'متصلة' : 'غير متصلة'}
              </span>
              {statusLabel && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium text-white"
                      style={{ backgroundColor: statusColor }}>
                  {statusLabel}
                </span>
              )}
              <p className="text-xs text-gray-500">
                آخر اتصال: {heardAt ? fmtFromNow(heardAt) : '—'}
              </p>
            </div>
          </div>

          {/* The identifying details, on one line under the name. */}
          <dl className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-3 gap-x-4 text-sm">
            <div>
              <dt className="text-gray-500 text-xs">الموديل</dt>
              <dd className="font-medium text-gray-900">{vehicle?.model || '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500 text-xs">سنة الصنع</dt>
              <dd className="font-medium text-gray-900">{vehicle?.year || '—'}</dd>
            </div>
            <div>
              <dt className="text-gray-500 text-xs">العدّاد الكلي</dt>
              <dd className="font-medium text-gray-900">
                {odoKm != null ? `${odoKm.toLocaleString()} كم` : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 text-xs">جهاز التتبع</dt>
              <dd className="font-medium text-gray-900">{vehicle?.deviceModel || '—'}</dd>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <dt className="text-gray-500 text-xs flex items-center gap-1">
                <IdentificationIcon className="w-3.5 h-3.5" /> IMEI
              </dt>
              <dd className="font-mono text-xs text-gray-900 text-right" dir="ltr">
                {vehicle?.deviceImei || '—'}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Active event chips */}
      {criticalChips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {criticalChips.map((c, i) => (
            <span key={i} className={clsx('inline-flex items-center gap-1 px-3 py-1 rounded-full border text-sm', c.tone)}>
              <c.icon className="w-4 h-4" />
              {c.label}
            </span>
          ))}
        </div>
      )}

      {/* The tiles below are the last packet's contents, not live telemetry.
          While a unit is out of contact they keep showing "engine running" in
          bright green, which reads as a live state — so say when it was taken. */}
      {!isOnline && heardAt && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 flex items-center gap-2">
          <ClockIcon className="w-4 h-4 text-gray-400 shrink-0" />
          <p className="text-sm text-gray-600">
            القراءات التالية من آخر اتصال ({fmtFromNow(heardAt)})، وليست لحظية
          </p>
        </div>
      )}

      {/* Top KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          label="حالة المحرك"
          value={io.ignition === true ? 'تشغيل' : io.ignition === false ? 'إيقاف' : '—'}
          sub={io.movement === true ? 'في حركة' : io.movement === false ? 'متوقف' : null}
          icon={BoltIcon}
          tone={io.ignition === true ? 'green' : 'gray'}
        />
        <StatCard
          label="السرعة"
          value={speedNum != null ? `${Math.round(speedNum)} كم/س` : '—'}
          sub={`الاتجاه: ${io.course != null ? Math.round(Number(io.course)) + '°' : '—'}`}
          icon={TruckIcon}
          tone={speedNum && speedNum > 100 ? 'red' : speedNum && speedNum > 80 ? 'amber' : 'blue'}
        />
        <StatCard
          label="مستوى البطارية"
          value={io.batteryLevel != null ? `${io.batteryLevel}%` : '—'}
          sub={intV ? `الفولتية الداخلية: ${intV}V` : null}
          icon={batteryIcon}
          tone={batteryTone}
        />
        <StatCard
          label="فولتية البطارية الخارجية"
          value={extV ? `${extV}V` : '—'}
          sub={io.externalPower === true ? 'متصل بالطاقة' : io.externalPower === false ? 'غير متصل' : null}
          icon={BoltIcon}
          tone={io.externalPower === true ? 'green' : 'red'}
        />
      </div>

      {/* Second KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="card bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <div className="card-body py-3 sm:py-4">
            <p className="text-white/80 text-xs sm:text-sm">إشارة GSM</p>
            <div className="mt-2"><SignalBars level={io.gsmSignal} /></div>
            <p className="text-white/70 text-xs mt-2">{io.gsmSignal != null ? `${io.gsmSignal}/5` : '—'}</p>
          </div>
        </div>
        <StatCard
          label="عداد المسافة الإجمالي"
          value={odoKm != null ? `${odoKm.toLocaleString()} كم` : '—'}
          sub={
            initialKm != null
              // Show the arithmetic, so the figure is auditable against the
              // reading someone entered rather than being a bare number.
              ? `${initialKm.toLocaleString()} كم مسجّلة + ${(travelledKm ?? 0).toLocaleString()} كم منذ التسجيل`
              : tripKm ? `الرحلة الحالية: ${tripKm} كم` : null
          }
          icon={MapPinIcon}
          tone="blue"
        />
        <StatCard
          label="الأقمار / دقة GPS"
          value={io.satellites != null ? `${io.satellites} قمر` : '—'}
          sub={io.gnssHdop ? `HDOP: ${Number(io.gnssHdop).toFixed(1)}` : null}
          icon={SignalIcon}
          tone={io.satellites && io.satellites >= 6 ? 'green' : 'amber'}
        />
        <StatCard
          label="حالة Relay (DOUT)"
          value={`${io.dout1 ? '1' : '0'} / ${io.dout2 ? '1' : '0'}`}
          sub={`DOUT1: ${io.dout1 ? 'مفعّل' : 'معطّل'} · DOUT2: ${io.dout2 ? 'مفعّل' : 'معطّل'}`}
          icon={Cog6ToothIcon}
          tone={io.dout2 ? 'red' : 'gray'}
        />
      </div>

      {/* Map + charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card overflow-hidden">
          <div className="card-header"><h3 className="font-semibold">آخر موقع</h3></div>
          <div className="h-72 sm:h-80">
            {lat != null && lng != null ? (
              <MapContainer center={[lat, lng]} zoom={15} style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[lat, lng]} icon={markerIcon}>
                  <Popup>
                    {vehicle?.plateNumber}<br />
                    {fmt(io.deviceTime)}
                  </Popup>
                </Marker>
              </MapContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">لا توجد إحداثيات</div>
            )}
          </div>
          <div className="card-body py-2 text-xs text-gray-500 flex flex-wrap gap-x-4">
            <span>آخر نقطة: {fmtFromNow(io.deviceTime)}</span>
            <span>إحداثيات: {lat?.toFixed(5)}, {lng?.toFixed(5)}</span>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3 className="font-semibold">السرعة خلال 24 ساعة</h3></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={(speedHistory || []).map((p: any) => ({ t: dayjs(p.t).tz(TZ).format('HH:mm'), v: p.v }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="t" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="v" stroke="#3b82f6" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="card-header"><h3 className="font-semibold">فولتية البطارية الخارجية (24س)</h3></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={(voltageHistory || []).map((p: any) => ({ t: dayjs(p.t).tz(TZ).format('HH:mm'), v: p.v ? p.v / 1000 : null }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="t" />
                <YAxis domain={['dataMin - 1', 'dataMax + 1']} />
                <Tooltip />
                <Line type="monotone" dataKey="v" stroke="#22c55e" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="font-semibold">آخر التنبيهات</h3>
            <Link to={`/alerts?vehicleId=${id}`} className="text-sm text-primary-600 hover:text-primary-700">عرض الكل</Link>
          </div>
          <div className="divide-y max-h-80 overflow-y-auto">
            {(alertsResp || []).length === 0 ? (
              <div className="p-6 text-center text-gray-400">لا توجد تنبيهات</div>
            ) : (
              (alertsResp || []).slice(0, 10).map((a: any) => (
                <div key={a.id} className="px-3 py-2 flex items-start gap-2 text-sm">
                  <span className={clsx(
                    'mt-1 w-2 h-2 rounded-full flex-shrink-0',
                    a.severity === 'critical' ? 'bg-red-500' :
                    a.severity === 'high' ? 'bg-orange-500' :
                    a.severity === 'medium' ? 'bg-yellow-500' : 'bg-green-500',
                  )} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900">{a.messageAr || a.message}</p>
                    <p className="text-xs text-gray-500">{fmtFromNow(a.createdAt)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Route review with playback */}
      <TrackPlayback
        vehicleId={id!}
        vehicleType={vehicle?.vehicleType || 'ambulance'}
        plateNumber={vehicle?.plateNumber || ''}
      />

      {/* Static details */}
      <div className="card">
        {/* Everything not already stated in the header block above. */}
        <div className="card-header"><h3 className="font-semibold">بيانات إضافية</h3></div>
        <div className="card-body grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-y-3 text-sm">
          <div><p className="text-gray-500">رقم الهيكل</p>
            <p className="font-mono text-xs text-right" dir="ltr">
              {vehicle?.chassisNumber || '—'}</p></div>
          <div><p className="text-gray-500">رقم الشريحة</p>
            <p className="font-mono text-xs text-right" dir="ltr">
              {vehicle?.simNumber || '—'}</p></div>
          <div><p className="text-gray-500">آخر صيانة</p><p>{fmt(vehicle?.lastMaintenanceDate, 'YYYY-MM-DD')}</p></div>
          <div><p className="text-gray-500">الصيانة القادمة</p><p>{fmt(vehicle?.nextMaintenanceDate, 'YYYY-MM-DD')}</p></div>
          <div>
            <p className="text-gray-500">المسافة المقطوعة المسجّلة</p>
            <p>{initialKm != null ? `${initialKm.toLocaleString()} كم` : '—'}</p>
            {odometer?.readAt && (
              <p className="text-xs text-gray-400">سُجّلت {fmt(odometer.readAt, 'YYYY-MM-DD')}</p>
            )}
          </div>
          <div>
            <p className="text-gray-500">المقطوعة منذ التسجيل</p>
            <p>{travelledKm != null ? `${travelledKm.toLocaleString()} كم` : '—'}</p>
            {odoSourceLabel && <p className="text-xs text-gray-400">{odoSourceLabel}</p>}
          </div>
          <div>
            <p className="text-gray-500">عدّاد الجهاز</p>
            <p>{deviceOdoKm != null ? `${deviceOdoKm.toLocaleString()} كم` : '—'}</p>
            {odometer?.deviceReset && (
              <p className="text-xs text-warning-600">أُعيد ضبط العدّاد — يُحتسب من مسار GPS</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
